import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe } from '@/lib/api';
import { Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleLogin = () => {
    // Store that this is an admin login attempt
    sessionStorage.setItem('authRedirect', '/admin');
    sessionStorage.setItem('adminLoginAttempt', 'true');
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const checkExistingSession = async () => {
    setLoading(true);
    try {
      const res = await getMe();
      if (res.data.role === 'admin') {
        navigate('/admin');
      } else {
        toast.error('You do not have admin access');
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  // Check if already logged in
  useState(() => {
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        const user = JSON.parse(cachedUser);
        if (user.role === 'admin') {
          navigate('/admin');
        }
      } catch (e) {
        // Invalid cached data, ignore
        console.log('Invalid cached user data');
      }
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-stone-50" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-stone-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-white" />
          </div>
          
          <h1 className="text-3xl font-medium mb-2">Admin Portal</h1>
          <p className="text-stone-600 mb-8">
            Sign in with your authorized admin account
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-8 w-8 animate-spin text-stone-900" />
            </div>
          ) : (
            <>
              <button
                onClick={handleLogin}
                className="w-full bg-stone-900 text-white py-4 px-6 rounded-full font-medium hover:bg-stone-800 transition-colors flex items-center justify-center"
                data-testid="admin-google-login"
              >
                <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              <button
                onClick={checkExistingSession}
                className="w-full mt-4 text-stone-600 py-3 hover:text-stone-900 transition-colors text-sm"
              >
                Already logged in? Check access →
              </button>
            </>
          )}

          <div className="mt-8 pt-6 border-t border-stone-200">
            <p className="text-xs text-stone-500">
              Only authorized admin accounts can access this portal.
              <br />
              Contact your administrator if you need access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
